/**
 * ProfileBlockEditorSheet (web): Gemini-styled alert dialog.
 *
 * Pure HTML/CSS surface for editing a single block:
 *  - Visibility toggle (segment of the settings card)
 *  - Title / subtitle / URL / body / button label / submit email pills
 *  - Source-data callout (website, location, web CV)
 *  - Portfolio / Service offer record lists with inline edit form
 *  - Delete action
 *
 * Mirrors the design system already used by the social/contact/add sheets:
 *   • centered WebDialog shell
 *   • neutral tinted cards `rgba(0,0,0,0.04)` and hover `rgba(0,0,0,0.06)`
 *   • white pill inputs (52h, var(--input-bg), var(--gem-input-shadow))
 *   • Material Symbols Outlined throughout
 *   • single accent (#5e29ff) deliberately omitted: destructive uses #d93025
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { WebDialog } from '../common/WebDialog.web';
import { MaterialSymbol } from '../icons/MaterialSymbol';
import { useToast } from '../../hooks/useToast';
import { useLocale } from '../../hooks/useLocale';
import {
  useDeleteProject,
  useDeleteServiceOffer,
  useUpsertProject,
  useUpsertServiceOffer,
} from '../../hooks/useProfileApi';
import { fromWebFile, uploadImageAsset } from '../../lib/image-upload';
import { confirm } from '../../lib/alert.web';
import {
  PUBLIC_PROFILE_BLOCK_META,
  isPublicProfileAuthoredBlock,
  isPublicProfileDataBackedBlock,
  isPublicProfilePermanentBlock,
  type PublicProfileBlock,
  type PublicProfileBlockType,
} from '../../lib/public-profile-builder';
import { getPublicProfileRoutes } from '../../lib/public-profile-routes';
import { usePublicProfileBuilderModel } from './PublicProfileBuilderProvider';

const MATERIAL_ICON_BY_TYPE: Record<PublicProfileBlockType, string> = {
  webcv: 'description',
  career: 'work',
  services: 'storefront',
  website: 'language',
  insights: 'insights',
  faqs: 'help',
  links: 'link',
  visit: 'place',
  openroles: 'work',
  team: 'groups',
  talentpool: 'groups_3',
  portfolio: 'grid_view',
  testimonial: 'reviews',
  booking: 'event',
  map: 'map',
  pdf: 'picture_as_pdf',
  image: 'image',
  video: 'play_circle',
  digitalproduct: 'inventory_2',
  text: 'subject',
  header: 'title',
  form: 'description',
  music: 'music_note',
};

type ProjectFormState = {
  id?: string;
  title: string;
  description: string;
  image: string;
  link: string;
  technologies: string;
};

type ServiceFormState = {
  id?: string;
  title: string;
  description: string;
  basePrice: string;
  deliveryDays: string;
  deliverables: string;
};

function emptyProjectForm(): ProjectFormState {
  return { title: '', description: '', image: '', link: '', technologies: '' };
}

function emptyServiceForm(): ServiceFormState {
  return {
    title: '',
    description: '',
    basePrice: '',
    deliveryDays: '',
    deliverables: '',
  };
}

function parseCommaSeparated(value: string) {
  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

async function pickWebImageFile(): Promise<File | null> {
  if (typeof document === 'undefined') return null;
  return new Promise<File | null>((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.style.display = 'none';
    input.onchange = () => {
      const file = input.files?.[0] ?? null;
      input.remove();
      resolve(file);
    };
    document.body.appendChild(input);
    input.click();
  });
}

export function ProfileBlockEditorSheet() {
  const { t } = useTranslation('common');
  const tx = useCallback((key: string) => String(t(key as never)), [t]);
  const toast = useToast();
  const router = useRouter();

  const params = useLocalSearchParams<{
    blockId?: string | string[];
    intent?: string | string[];
  }>();
  const rawBlockId = Array.isArray(params.blockId)
    ? params.blockId[0]
    : params.blockId;
  const blockId =
    typeof rawBlockId === 'string' ? decodeURIComponent(rawBlockId) : '';
  const rawIntent = Array.isArray(params.intent)
    ? params.intent[0]
    : params.intent;
  const intent = typeof rawIntent === 'string' ? rawIntent : '';

  const {
    blocks,
    setBlocks,
    projects,
    serviceOffers,
    routeMode,
    draftProfile,
    retry,
    isHydrated,
  } = usePublicProfileBuilderModel();
  const profileRoutes = useMemo(
    () => getPublicProfileRoutes(routeMode),
    [routeMode],
  );
  const block = useMemo(
    () => blocks.find((item) => item.id === blockId) ?? null,
    [blockId, blocks],
  );

  const upsertProject = useUpsertProject();
  const deleteProject = useDeleteProject();
  const upsertServiceOffer = useUpsertServiceOffer();
  const deleteServiceOffer = useDeleteServiceOffer();

  const [editingProjectId, setEditingProjectId] = useState<
    string | 'new' | null
  >(null);
  const [editingServiceId, setEditingServiceId] = useState<
    string | 'new' | null
  >(null);
  const [projectForm, setProjectForm] = useState<ProjectFormState>(
    emptyProjectForm,
  );
  const [serviceForm, setServiceForm] = useState<ServiceFormState>(
    emptyServiceForm,
  );
  const [isUploadingProjectImage, setIsUploadingProjectImage] = useState(false);

  // Hydrate each editor once per open. `projects` / `serviceOffers` get a fresh
  // identity on every profile refetch, and re-running on that identity re-filled
  // the form from the server mid-edit, wiping whatever had been typed.
  const hydratedProjectIdRef = useRef<string | 'new' | null>(null);
  const hydratedServiceIdRef = useRef<string | 'new' | null>(null);

  useEffect(() => {
    if (hydratedProjectIdRef.current === editingProjectId) {
      return;
    }
    if (editingProjectId === null || editingProjectId === 'new') {
      hydratedProjectIdRef.current = editingProjectId;
      setProjectForm(emptyProjectForm());
      return;
    }
    const project = projects.find((item) => item.id === editingProjectId);
    if (!project) {
      setEditingProjectId(null);
      return;
    }
    hydratedProjectIdRef.current = editingProjectId;
    setProjectForm({
      id: project.id,
      title: project.title,
      description: project.description ?? '',
      image: project.image ?? '',
      link: project.link ?? '',
      technologies: (project.technologies ?? []).join(', '),
    });
  }, [editingProjectId, projects]);

  useEffect(() => {
    if (hydratedServiceIdRef.current === editingServiceId) {
      return;
    }
    if (editingServiceId === null || editingServiceId === 'new') {
      hydratedServiceIdRef.current = editingServiceId;
      setServiceForm(emptyServiceForm());
      return;
    }
    const service = serviceOffers.find((item) => item.id === editingServiceId);
    if (!service) {
      setEditingServiceId(null);
      return;
    }
    hydratedServiceIdRef.current = editingServiceId;
    setServiceForm({
      id: service.id,
      title: service.title,
      description: service.description ?? '',
      basePrice:
        typeof service.basePrice === 'number' &&
        Number.isFinite(service.basePrice)
          ? String(service.basePrice)
          : '',
      deliveryDays:
        typeof service.deliveryDays === 'number' &&
        Number.isFinite(service.deliveryDays)
          ? String(service.deliveryDays)
          : '',
      deliverables: (service.deliverables ?? []).join(', '),
    });
  }, [editingServiceId, serviceOffers]);

  useEffect(() => {
    if (!block || intent !== 'create-source') return;
    if (
      block.type === 'portfolio' &&
      projects.length === 0 &&
      editingProjectId === null
    ) {
      setEditingProjectId('new');
    }
    if (
      block.type === 'services' &&
      serviceOffers.length === 0 &&
      editingServiceId === null
    ) {
      setEditingServiceId('new');
    }
  }, [
    block,
    editingProjectId,
    editingServiceId,
    intent,
    projects.length,
    serviceOffers.length,
  ]);

  const closeDialog = useCallback(() => {
    if (typeof window !== 'undefined' && window.history.length > 1) {
      router.back();
      return;
    }
    router.replace(profileRoutes.index as never);
  }, [profileRoutes.index, router]);

  const updateBlock = useCallback(
    (patch: Partial<PublicProfileBlock>) => {
      if (!block) return;
      setBlocks((previous) =>
        previous.map((item) =>
          item.id === block.id
            ? {
                ...item,
                ...patch,
                config: { ...item.config, ...patch.config },
              }
            : item,
        ),
      );
    },
    [block, setBlocks],
  );

  const handleDeleteBlock = useCallback(async () => {
    if (!block) return;
    const confirmed = await confirm({
      title: tx('publicProfile.blockEditor.deleteBlock'),
      message: tx('publicProfile.blockEditor.deleteConfirm'),
      confirmLabel: tx('actions.remove'),
      cancelLabel: tx('actions.cancel'),
      destructive: true,
    });
    if (!confirmed) return;
    setBlocks((previous) => previous.filter((item) => item.id !== block.id));
    toast.show({ title: tx('publicProfile.blockEditor.deleteSuccess') });
    router.replace(profileRoutes.index as never);
  }, [block, profileRoutes.index, router, setBlocks, toast, tx]);

  const uploadProjectImage = useCallback(async () => {
    try {
      setIsUploadingProjectImage(true);
      const picked = await pickWebImageFile();
      if (!picked) return;
      const normalizedAsset = fromWebFile(picked);
      const fileUrl = await uploadImageAsset(normalizedAsset, {
        kind: 'featured-project-image',
        fileName: normalizedAsset.name ?? null,
        mimeType: normalizedAsset.type ?? null,
      });
      setProjectForm((previous) => ({ ...previous, image: fileUrl }));
      toast.show({ title: tx('publicProfile.blockEditor.project.imageUploaded') });
    } catch (error) {
      toast.show({
        title:
          error instanceof Error
            ? error.message
            : tx('publicProfile.blockEditor.project.imageUploadFailed'),
      });
    } finally {
      setIsUploadingProjectImage(false);
    }
  }, [toast, tx]);

  const saveProject = useCallback(async () => {
    if (!projectForm.title.trim()) {
      toast.show({ title: tx('publicProfile.blockEditor.project.titleRequired') });
      return;
    }
    try {
      await upsertProject.mutateAsync({
        id: projectForm.id,
        title: projectForm.title.trim(),
        description: projectForm.description.trim() || undefined,
        image: projectForm.image.trim() || undefined,
        link: projectForm.link.trim() || undefined,
        technologies: parseCommaSeparated(projectForm.technologies),
        order:
          typeof projects.find((item) => item.id === projectForm.id)?.order ===
          'number'
            ? projects.find((item) => item.id === projectForm.id)?.order
            : projects.length,
      });
      toast.show({ title: tx('publicProfile.blockEditor.project.saved') });
      setEditingProjectId(null);
      void retry();
    } catch (error) {
      toast.show({
        title:
          error instanceof Error
            ? error.message
            : tx('publicProfile.blockEditor.project.saveFailed'),
      });
    }
  }, [projectForm, projects, retry, toast, tx, upsertProject]);

  const removeProject = useCallback(
    async (id: string) => {
      try {
        await deleteProject.mutateAsync(id);
        toast.show({ title: tx('publicProfile.blockEditor.project.deleted') });
        if (editingProjectId === id) setEditingProjectId(null);
      } catch (error) {
        toast.show({
          title:
            error instanceof Error
              ? error.message
              : tx('publicProfile.blockEditor.project.deleteFailed'),
        });
      }
    },
    [deleteProject, editingProjectId, toast, tx],
  );

  const saveService = useCallback(async () => {
    if (!serviceForm.title.trim()) {
      toast.show({ title: tx('publicProfile.blockEditor.service.titleRequired') });
      return;
    }
    const basePrice = serviceForm.basePrice.trim()
      ? Number(serviceForm.basePrice.trim())
      : undefined;
    const deliveryDays = serviceForm.deliveryDays.trim()
      ? Number(serviceForm.deliveryDays.trim())
      : undefined;
    if (
      (basePrice !== undefined && Number.isNaN(basePrice)) ||
      (deliveryDays !== undefined && Number.isNaN(deliveryDays))
    ) {
      toast.show({ title: tx('publicProfile.blockEditor.service.numberError') });
      return;
    }
    try {
      await upsertServiceOffer.mutateAsync({
        id: serviceForm.id,
        title: serviceForm.title.trim(),
        description: serviceForm.description.trim() || undefined,
        basePrice: basePrice ?? null,
        deliveryDays: deliveryDays ?? null,
        deliverables: parseCommaSeparated(serviceForm.deliverables),
        order:
          typeof serviceOffers.find((item) => item.id === serviceForm.id)
            ?.order === 'number'
            ? serviceOffers.find((item) => item.id === serviceForm.id)?.order
            : serviceOffers.length,
      });
      toast.show({ title: tx('publicProfile.blockEditor.service.saved') });
      setEditingServiceId(null);
      void retry();
    } catch (error) {
      toast.show({
        title:
          error instanceof Error
            ? error.message
            : tx('publicProfile.blockEditor.service.saveFailed'),
      });
    }
  }, [retry, serviceForm, serviceOffers, toast, tx, upsertServiceOffer]);

  const removeService = useCallback(
    async (id: string) => {
      try {
        await deleteServiceOffer.mutateAsync(id);
        toast.show({ title: tx('publicProfile.blockEditor.service.deleted') });
        if (editingServiceId === id) setEditingServiceId(null);
      } catch (error) {
        toast.show({
          title:
            error instanceof Error
              ? error.message
              : tx('publicProfile.blockEditor.service.deleteFailed'),
        });
      }
    },
    [deleteServiceOffer, editingServiceId, toast, tx],
  );

  // Empty state when block doesn't resolve
  if (!block && isHydrated) {
    return (
      <WebDialog
        title={tx('publicProfile.blockEditor.title')}
        subtitle={tx('publicProfile.blockEditor.subtitle')}
        onClose={closeDialog}
        maxWidth={560}
      >
        <EmptyCard
          icon="link_off"
          title={tx('publicProfile.blockEditor.notFoundTitle')}
          body={tx('publicProfile.blockEditor.notFoundBody')}
          actionLabel={tx('actions.back')}
          onAction={() => router.replace(profileRoutes.index as never)}
        />
      </WebDialog>
    );
  }

  if (!block) return null;

  const blockMeta = PUBLIC_PROFILE_BLOCK_META[block.type];
  const blockTitle = tx(blockMeta.titleKey);
  const blockIcon = MATERIAL_ICON_BY_TYPE[block.type] ?? 'apps';

  // Field visibility: driven by what the renderer actually consumes for this
  // block type. See packages/types/src/public-profile.ts buildPublicProfileRenderBlocks.
  const showLinkField = [
    'form',
    'video',
    'music',
    'pdf',
    'image',
    'digitalproduct',
    'booking',
    'talentpool',
    'insights',
  ].includes(block.type);
  const linkPlaceholder =
    block.type === 'video'
      ? 'YouTube or Vimeo URL'
      : block.type === 'music'
        ? 'Spotify or SoundCloud URL'
        : block.type === 'pdf'
          ? 'PDF file URL'
          : block.type === 'image'
            ? 'Optional link when image is clicked'
            : block.type === 'booking'
              ? 'Booking/scheduling URL'
              : block.type === 'insights'
                ? 'Blog or article URL'
                : block.type === 'talentpool'
                  ? 'Talent pool form URL'
                  : block.type === 'digitalproduct'
                    ? 'Product or checkout URL'
                    : tx('publicProfile.blockEditor.urlPlaceholder');
  const linkLabel =
    block.type === 'video'
      ? 'Video URL'
      : block.type === 'music'
        ? 'Music URL'
        : block.type === 'pdf'
          ? 'PDF URL'
          : block.type === 'image'
            ? 'Click destination'
            : block.type === 'digitalproduct'
              ? 'Buy URL'
              : block.type === 'booking'
                ? 'Booking URL'
                : block.type === 'insights'
                  ? 'Article URL'
                  : block.type === 'talentpool'
                    ? 'Apply URL'
                    : 'Link';

  const showImageField = ['image', 'pdf', 'digitalproduct'].includes(block.type);
  const imageFieldLabel =
    block.type === 'image'
      ? 'Image'
      : block.type === 'pdf'
        ? 'Cover image (optional)'
        : 'Product image';

  // Body is now shown for ALL blocks. The label changes per block to keep the
  // intent clear: "Body" for text blocks, "Description" everywhere else.
  const bodyLabel = block.type === 'text' ? 'Body' : 'Description';

  const showButtonLabelField = [
    'form',
    'booking',
    'talentpool',
    'digitalproduct',
    'insights',
    'pdf',
    'video',
    'music',
  ].includes(block.type);
  const showSubmitEmailField = block.type === 'form';
  const showItemsField = ['faqs', 'testimonial', 'links'].includes(block.type);
  const itemsLabel = tx(
    block.type === 'faqs'
      ? 'publicProfile.blockEditor.faqsLabel'
      : block.type === 'links'
        ? 'publicProfile.blockEditor.linksLabel'
        : 'publicProfile.blockEditor.testimonialsLabel',
  );

  return (
    <WebDialog
      title={blockTitle}
      subtitle={tx('publicProfile.blockEditor.subtitle')}
      onClose={closeDialog}
      maxWidth={620}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        {/* Hero: type icon + name */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            padding: '14px 16px',
            background: 'rgba(0,0,0,0.04)',
            borderRadius: 20,
          }}
        >
          <div
            style={{
              width: 44,
              height: 44,
              borderRadius: 22,
              background: 'rgba(0,0,0,0.06)',
              color: 'var(--text-primary)',
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <MaterialSymbol name={blockIcon} size={20} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 15,
                fontWeight: 500,
                color: 'var(--text-primary)',
                fontFamily:
                  "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
              }}
            >
              {blockTitle}
            </div>
            <div
              style={{
                fontSize: 13,
                color: 'var(--text-tertiary)',
                fontFamily:
                  "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
              }}
            >
              {tx(blockMeta.descKey)}
            </div>
          </div>
          <ToggleSwitch
            checked={block.enabled}
            onToggle={() => updateBlock({ enabled: !block.enabled })}
          />
        </div>

        {/* Settings */}
        <Section title={tx('publicProfile.blockEditor.settings')}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <FieldGroup label="Title">
              <PillInput
                value={block.customTitle ?? ''}
                onChange={(value) => updateBlock({ customTitle: value })}
                placeholder={tx('publicProfile.blockEditor.titlePlaceholder')}
              />
            </FieldGroup>
            <FieldGroup label="Subtitle">
              <PillInput
                value={block.customSubtitle ?? ''}
                onChange={(value) => updateBlock({ customSubtitle: value })}
                placeholder={tx('publicProfile.blockEditor.subtitlePlaceholder')}
              />
            </FieldGroup>
            <FieldGroup label={bodyLabel}>
              <PillTextarea
                value={block.config?.body ?? ''}
                onChange={(value) =>
                  updateBlock({ config: { ...block.config, body: value } })
                }
                placeholder={
                  block.type === 'text'
                    ? tx('publicProfile.blockEditor.bodyPlaceholder')
                    : 'Optional description shown beneath the title'
                }
              />
            </FieldGroup>
            {showLinkField && (
              <FieldGroup label={linkLabel}>
                <PillInput
                  value={block.config?.url ?? ''}
                  onChange={(value) =>
                    updateBlock({ config: { ...block.config, url: value } })
                  }
                  placeholder={linkPlaceholder}
                  inputType="url"
                />
              </FieldGroup>
            )}
            {showImageField && (
              <FieldGroup label={imageFieldLabel}>
                <ImageUrlField
                  value={block.config?.imageUrl ?? ''}
                  onChange={(value) =>
                    updateBlock({ config: { ...block.config, imageUrl: value } })
                  }
                  placeholder="Paste an image URL or upload a file"
                />
              </FieldGroup>
            )}
            {showButtonLabelField && (
              <FieldGroup label="Button label">
                <PillInput
                  value={block.config?.buttonLabel ?? ''}
                  onChange={(value) =>
                    updateBlock({
                      config: { ...block.config, buttonLabel: value },
                    })
                  }
                  placeholder={tx(
                    'publicProfile.blockEditor.buttonLabelPlaceholder',
                  )}
                />
              </FieldGroup>
            )}
            {showSubmitEmailField && (
              <FieldGroup label="Where to send submissions">
                <PillInput
                  value={block.config?.submitEmail ?? ''}
                  onChange={(value) =>
                    updateBlock({
                      config: { ...block.config, submitEmail: value },
                    })
                  }
                  placeholder={tx(
                    'publicProfile.blockEditor.submitEmailPlaceholder',
                  )}
                  inputType="email"
                />
              </FieldGroup>
            )}
            {showItemsField && (
              <FieldGroup label={itemsLabel}>
                <BlockItemsEditor
                  blockType={block.type}
                  tx={tx}
                  items={block.config?.items ?? []}
                  onChange={(items) =>
                    updateBlock({ config: { ...block.config, items } })
                  }
                />
              </FieldGroup>
            )}
          </div>
        </Section>

        {/* Source callout */}
        {['website', 'visit', 'map'].includes(block.type) && (
          <SourceCallout
            icon={block.type === 'website' ? 'link' : 'place'}
            title={tx(
              block.type === 'website'
                ? 'publicProfile.blockEditor.websiteSourceTitle'
                : 'publicProfile.blockEditor.locationSourceTitle',
            )}
            body={tx(
              block.type === 'website'
                ? 'publicProfile.blockEditor.websiteSourceBody'
                : 'publicProfile.blockEditor.locationSourceBody',
            )}
            valueLabel={
              (block.type === 'website'
                ? draftProfile.website.trim()
                : draftProfile.location.trim()) ||
              tx(
                block.type === 'website'
                  ? 'publicProfile.blockEditor.websiteSourceEmpty'
                  : 'publicProfile.blockEditor.locationSourceEmpty',
              )
            }
            ctaLabel={tx('publicProfile.blockEditor.sourceLinkCta')}
            onCta={() => router.push(profileRoutes.editProfile as never)}
          />
        )}

        {/* CV source */}
        {block.type === 'webcv' && (
          <SourceCallout
            icon="description"
            title={tx('publicProfile.blockEditor.webcv.title')}
            body={String(
              t('publicProfile.blockEditor.webcv.body' as never, {
                name: draftProfile.name || tx('publicProfile.defaultName'),
              }),
            )}
            ctaLabel={tx('publicProfile.blockEditor.webcv.cta')}
            onCta={() => router.push(profileRoutes.editProfile as never)}
          />
        )}

        {/* Portfolio source data */}
        {block.type === 'portfolio' && (
          <Section title={tx('publicProfile.blockEditor.sourceData')}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {projects.length === 0 ? (
                <EmptyCard
                  icon="grid_view"
                  title={tx('publicProfile.blockEditor.project.emptyTitle')}
                  body={tx('publicProfile.blockEditor.project.emptyBody')}
                />
              ) : (
                projects.map((project) => (
                  <RecordCard
                    key={project.id}
                    title={project.title}
                    body={
                      project.description ||
                      tx('publicProfile.blockEditor.project.noDescription')
                    }
                    imageUrl={project.image ?? ''}
                    onEdit={() => setEditingProjectId(project.id)}
                    onRemove={() => {
                      void removeProject(project.id);
                    }}
                    editLabel={tx('actions.edit')}
                    removeLabel={tx('actions.remove')}
                  />
                ))
              )}
              {!editingProjectId ? (
                <NeutralButton
                  icon="add"
                  label={
                    projects.length === 0
                      ? tx('publicProfile.blockEditor.project.add')
                      : tx('publicProfile.blockEditor.project.addAnother')
                  }
                  onPress={() => setEditingProjectId('new')}
                />
              ) : null}
              {editingProjectId ? (
                <FormCard
                  title={
                    editingProjectId === 'new'
                      ? tx('publicProfile.blockEditor.project.addTitle')
                      : tx('publicProfile.blockEditor.project.editTitle')
                  }
                >
                  <PillInput
                    value={projectForm.title}
                    onChange={(value) =>
                      setProjectForm((previous) => ({
                        ...previous,
                        title: value,
                      }))
                    }
                    placeholder={tx(
                      'publicProfile.blockEditor.project.titlePlaceholder',
                    )}
                  />
                  <PillTextarea
                    value={projectForm.description}
                    onChange={(value) =>
                      setProjectForm((previous) => ({
                        ...previous,
                        description: value,
                      }))
                    }
                    placeholder={tx(
                      'publicProfile.blockEditor.project.descriptionPlaceholder',
                    )}
                  />
                  <PillInput
                    value={projectForm.link}
                    onChange={(value) =>
                      setProjectForm((previous) => ({
                        ...previous,
                        link: value,
                      }))
                    }
                    placeholder={tx(
                      'publicProfile.blockEditor.project.linkPlaceholder',
                    )}
                    inputType="url"
                  />
                  <PillInput
                    value={projectForm.technologies}
                    onChange={(value) =>
                      setProjectForm((previous) => ({
                        ...previous,
                        technologies: value,
                      }))
                    }
                    placeholder={tx(
                      'publicProfile.blockEditor.project.technologiesPlaceholder',
                    )}
                  />
                  <PillInput
                    value={projectForm.image}
                    onChange={(value) =>
                      setProjectForm((previous) => ({
                        ...previous,
                        image: value,
                      }))
                    }
                    placeholder={tx(
                      'publicProfile.blockEditor.project.imagePlaceholder',
                    )}
                    inputType="url"
                  />
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 8,
                      marginTop: 4,
                    }}
                  >
                    <NeutralButton
                      icon="upload"
                      label={tx(
                        'publicProfile.blockEditor.project.uploadImage',
                      )}
                      onPress={() => {
                        void uploadProjectImage();
                      }}
                      loading={isUploadingProjectImage}
                    />
                    <PrimaryButton
                      label={tx('publicProfile.blockEditor.project.saveCta')}
                      onPress={() => {
                        void saveProject();
                      }}
                      loading={upsertProject.isPending}
                    />
                    <GhostButton
                      label={tx('actions.cancel')}
                      onPress={() => setEditingProjectId(null)}
                    />
                  </div>
                </FormCard>
              ) : null}
            </div>
          </Section>
        )}

        {/* Services source data */}
        {block.type === 'services' && (
          <Section title={tx('publicProfile.blockEditor.sourceData')}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {serviceOffers.length === 0 ? (
                <EmptyCard
                  icon="storefront"
                  title={tx('publicProfile.blockEditor.service.emptyTitle')}
                  body={tx('publicProfile.blockEditor.service.emptyBody')}
                />
              ) : (
                serviceOffers.map((service) => {
                  const meta =
                    service.basePrice != null
                      ? tx('publicProfile.blockEditor.service.priceAndTime')
                          .replace('{{price}}', String(service.basePrice))
                          .replace(
                            '{{days}}',
                            service.deliveryDays != null
                              ? String(service.deliveryDays)
                              : 'N/A',
                          )
                      : tx('publicProfile.blockEditor.service.timeOnly').replace(
                          '{{days}}',
                          service.deliveryDays != null
                            ? String(service.deliveryDays)
                            : 'N/A',
                        );
                  return (
                    <RecordCard
                      key={service.id}
                      title={service.title}
                      body={
                        service.description ||
                        tx('publicProfile.blockEditor.service.noDescription')
                      }
                      meta={meta}
                      onEdit={() => setEditingServiceId(service.id)}
                      onRemove={() => {
                        void removeService(service.id);
                      }}
                      editLabel={tx('actions.edit')}
                      removeLabel={tx('actions.remove')}
                    />
                  );
                })
              )}
              {!editingServiceId ? (
                <NeutralButton
                  icon="add"
                  label={
                    serviceOffers.length === 0
                      ? tx('publicProfile.blockEditor.service.add')
                      : tx('publicProfile.blockEditor.service.addAnother')
                  }
                  onPress={() => setEditingServiceId('new')}
                />
              ) : null}
              {editingServiceId ? (
                <FormCard
                  title={
                    editingServiceId === 'new'
                      ? tx('publicProfile.blockEditor.service.addTitle')
                      : tx('publicProfile.blockEditor.service.editTitle')
                  }
                >
                  <PillInput
                    value={serviceForm.title}
                    onChange={(value) =>
                      setServiceForm((previous) => ({
                        ...previous,
                        title: value,
                      }))
                    }
                    placeholder={tx(
                      'publicProfile.blockEditor.service.titlePlaceholder',
                    )}
                  />
                  <PillTextarea
                    value={serviceForm.description}
                    onChange={(value) =>
                      setServiceForm((previous) => ({
                        ...previous,
                        description: value,
                      }))
                    }
                    placeholder={tx(
                      'publicProfile.blockEditor.service.descriptionPlaceholder',
                    )}
                  />
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <PillInput
                      value={serviceForm.basePrice}
                      onChange={(value) =>
                        setServiceForm((previous) => ({
                          ...previous,
                          basePrice: value,
                        }))
                      }
                      placeholder={tx(
                        'publicProfile.blockEditor.service.basePricePlaceholder',
                      )}
                      inputType="number"
                    />
                    <PillInput
                      value={serviceForm.deliveryDays}
                      onChange={(value) =>
                        setServiceForm((previous) => ({
                          ...previous,
                          deliveryDays: value,
                        }))
                      }
                      placeholder={tx(
                        'publicProfile.blockEditor.service.deliveryDaysPlaceholder',
                      )}
                      inputType="number"
                    />
                  </div>
                  <PillTextarea
                    value={serviceForm.deliverables}
                    onChange={(value) =>
                      setServiceForm((previous) => ({
                        ...previous,
                        deliverables: value,
                      }))
                    }
                    placeholder={tx(
                      'publicProfile.blockEditor.service.deliverablesPlaceholder',
                    )}
                  />
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 8,
                      marginTop: 4,
                    }}
                  >
                    <PrimaryButton
                      label={tx('publicProfile.blockEditor.service.saveCta')}
                      onPress={() => {
                        void saveService();
                      }}
                      loading={upsertServiceOffer.isPending}
                    />
                    <GhostButton
                      label={tx('actions.cancel')}
                      onPress={() => setEditingServiceId(null)}
                    />
                  </div>
                </FormCard>
              ) : null}
            </div>
          </Section>
        )}

        {/* CV/dataset hint for other data-backed types */}
        {isPublicProfileDataBackedBlock(block.type) &&
          !['portfolio', 'services', 'webcv'].includes(block.type) && (
            <SourceCallout
              icon="auto_awesome"
              title={tx('publicProfile.blockEditor.authoredTitle')}
              body={tx('publicProfile.blockEditor.authoredBody')}
            />
          )}

        {/* Authored hint */}
        {isPublicProfileAuthoredBlock(block.type) && (
          <SourceCallout
            icon="auto_awesome"
            title={tx('publicProfile.blockEditor.authoredTitle')}
            body={tx('publicProfile.blockEditor.authoredBody')}
          />
        )}

        {/* Destructive — hidden for blocks that cannot be added back */}
        {!isPublicProfilePermanentBlock(block.type, routeMode) && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-start',
              paddingTop: 4,
            }}
          >
            <DestructiveButton
              icon="delete"
              label={tx('publicProfile.blockEditor.deleteBlock')}
              onPress={handleDeleteBlock}
            />
          </div>
        )}
      </div>
    </WebDialog>
  );
}

// ---------- presentational helpers ----------

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div
        style={{
          fontSize: 13,
          fontWeight: 500,
          color: 'var(--text-secondary)',
          padding: '0 4px',
          fontFamily:
            "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
        }}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

// Wraps a labelled form field. Surfaces the field's intent without forcing the
// user to read placeholders or remember which input they're on: the user's
// "form inputs don't show titles" complaint maps to this missing affordance.
function FieldGroup({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div
        style={{
          fontSize: 11.5,
          fontWeight: 500,
          color: 'var(--text-tertiary)',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          padding: '0 4px',
          fontFamily:
            "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
        }}
      >
        {label}
      </div>
      {children}
    </div>
  );
}

// Image URL input with inline upload button: reuses the same upload pipeline
// as portfolio project images so users don't need to host their own files.
function ImageUrlField({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (next: string) => void;
  placeholder: string;
}) {
  const [uploading, setUploading] = useState(false);
  const handleUpload = useCallback(async () => {
    try {
      setUploading(true);
      const picked = await pickWebImageFile();
      if (!picked) return;
      const normalized = fromWebFile(picked);
      const fileUrl = await uploadImageAsset(normalized, {
        kind: 'featured-project-image',
        fileName: normalized.name ?? null,
        mimeType: normalized.type ?? null,
      });
      onChange(fileUrl);
    } catch {
      // Upload errors are surfaced via toast elsewhere; here we just stop the spinner.
    } finally {
      setUploading(false);
    }
  }, [onChange]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <PillInput
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        inputType="url"
      />
      {value ? (
        <div
          style={{
            width: '100%',
            maxHeight: 180,
            overflow: 'hidden',
            borderRadius: 14,
            background: 'rgba(0,0,0,0.04)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <img
            src={value}
            alt=""
            style={{ display: 'block', maxWidth: '100%', maxHeight: 180 }}
          />
        </div>
      ) : null}
      <div>
        <NeutralButton
          icon={uploading ? 'progress_activity' : 'upload'}
          label={uploading ? 'Uploading…' : 'Upload image'}
          onPress={() => {
            void handleUpload();
          }}
          loading={uploading}
        />
      </div>
    </div>
  );
}

type BlockItem = {
  id: string;
  title?: string | null;
  body?: string | null;
  url?: string | null;
  imageUrl?: string | null;
  meta?: string | null;
};

// Inline editor for FAQs / testimonial items. Keeps the items inside the
// block's `config.items` JSON column: no extra Prisma table needed.
/**
 * Row editor for the item-backed blocks.
 *
 * `links` rows are a label plus a destination, so they swap the free-text body
 * for a URL field — a link row with no URL is not a link, and the publish gate
 * in `getPublishedPublicProfileBlocks` drops it.
 */
function BlockItemsEditor({
  blockType,
  tx,
  items,
  onChange,
}: {
  blockType: PublicProfileBlockType;
  tx: (key: string) => string;
  items: BlockItem[];
  onChange: (next: BlockItem[]) => void;
}) {
  const isFaq = blockType === 'faqs';
  const isLinks = blockType === 'links';
  const titleLabel = tx(
    isLinks
      ? 'publicProfile.blockEditor.linkTitle'
      : isFaq
        ? 'publicProfile.blockEditor.faqQuestion'
        : 'publicProfile.blockEditor.testimonialAuthor',
  );
  const bodyLabel = tx(
    isFaq
      ? 'publicProfile.blockEditor.faqAnswer'
      : 'publicProfile.blockEditor.testimonialQuote',
  );
  const metaLabel =
    isFaq || isLinks ? null : tx('publicProfile.blockEditor.testimonialMeta');
  const titlePlaceholder = tx(
    isLinks
      ? 'publicProfile.blockEditor.linkTitlePlaceholder'
      : isFaq
        ? 'publicProfile.blockEditor.faqQuestionPlaceholder'
        : 'publicProfile.blockEditor.testimonialAuthorPlaceholder',
  );
  const bodyPlaceholder = tx(
    isFaq
      ? 'publicProfile.blockEditor.faqAnswerPlaceholder'
      : 'publicProfile.blockEditor.testimonialQuotePlaceholder',
  );
  const metaPlaceholder = isFaq
    ? ''
    : tx('publicProfile.blockEditor.testimonialMetaPlaceholder');

  const addItem = useCallback(() => {
    onChange([
      ...items,
      {
        id: `item_${Date.now()}_${Math.floor(Math.random() * 1000)}`,
        title: '',
        ...(isLinks ? { url: '' } : { body: '' }),
        ...(metaLabel ? { meta: '' } : {}),
      },
    ]);
  }, [isLinks, items, metaLabel, onChange]);

  const updateItem = useCallback(
    (id: string, patch: Partial<BlockItem>) => {
      onChange(items.map((item) => (item.id === id ? { ...item, ...patch } : item)));
    },
    [items, onChange],
  );

  const removeItem = useCallback(
    (id: string) => {
      onChange(items.filter((item) => item.id !== id));
    },
    [items, onChange],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {items.length === 0 ? (
        <div
          style={{
            padding: '16px 18px',
            borderRadius: 16,
            background: 'rgba(0,0,0,0.04)',
            color: 'var(--text-tertiary)',
            fontSize: 13,
            lineHeight: '18px',
            textAlign: 'center',
            fontFamily:
              "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
          }}
        >
          {tx(
            isLinks
              ? 'publicProfile.blockEditor.linksEmpty'
              : isFaq
                ? 'publicProfile.blockEditor.faqsEmpty'
                : 'publicProfile.blockEditor.testimonialsEmpty',
          )}
        </div>
      ) : (
        items.map((item) => (
          <div
            key={item.id}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              padding: 14,
              borderRadius: 18,
              background: 'rgba(0,0,0,0.04)',
            }}
          >
            <FieldGroup label={titleLabel}>
              <PillInput
                value={item.title ?? ''}
                onChange={(value) => updateItem(item.id, { title: value })}
                placeholder={titlePlaceholder}
              />
            </FieldGroup>
            {isLinks ? (
              <FieldGroup label={tx('publicProfile.blockEditor.linkUrl')}>
                <PillInput
                  value={item.url ?? ''}
                  onChange={(value) => updateItem(item.id, { url: value })}
                  placeholder={tx(
                    'publicProfile.blockEditor.linkUrlPlaceholder',
                  )}
                  inputType="url"
                />
              </FieldGroup>
            ) : (
              <FieldGroup label={bodyLabel}>
                <PillTextarea
                  value={item.body ?? ''}
                  onChange={(value) => updateItem(item.id, { body: value })}
                  placeholder={bodyPlaceholder}
                />
              </FieldGroup>
            )}
            {metaLabel ? (
              <FieldGroup label={metaLabel}>
                <PillInput
                  value={item.meta ?? ''}
                  onChange={(value) => updateItem(item.id, { meta: value })}
                  placeholder={metaPlaceholder}
                />
              </FieldGroup>
            ) : null}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <DestructiveButton
                icon="delete"
                label={tx('actions.remove')}
                onPress={() => removeItem(item.id)}
              />
            </div>
          </div>
        ))
      )}
      <div>
        <NeutralButton
          icon="add"
          label={tx(
            isLinks
              ? 'publicProfile.blockEditor.addLink'
              : isFaq
                ? 'publicProfile.blockEditor.addQuestion'
                : 'publicProfile.blockEditor.addTestimonial',
          )}
          onPress={addItem}
        />
      </div>
    </div>
  );
}

function PillInput({
  value,
  onChange,
  placeholder,
  inputType = 'text',
}: {
  value: string;
  onChange: (next: string) => void;
  placeholder: string;
  inputType?: 'text' | 'url' | 'email' | 'number';
}) {
  const [focused, setFocused] = useState(false);
  return (
    <input
      value={value}
      type={inputType}
      onChange={(e) => onChange(e.target.value)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      placeholder={placeholder}
      spellCheck={false}
      autoCapitalize={inputType === 'text' ? 'sentences' : 'none'}
      autoCorrect="off"
      style={{
        width: '100%',
        height: 48,
        borderRadius: 9999,
        padding: '0 18px',
        fontSize: 14,
        color: 'var(--text-primary)',
        background: 'var(--input-bg)',
        border: focused
          ? '1px solid var(--text-secondary)'
          : '1px solid var(--input-border)',
        boxShadow: focused ? 'none' : 'var(--gem-input-shadow)',
        outline: 'none',
        transition: 'border-color 140ms, box-shadow 140ms',
        boxSizing: 'border-box',
        fontFamily:
          "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
      }}
    />
  );
}

function PillTextarea({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (next: string) => void;
  placeholder: string;
}) {
  const [focused, setFocused] = useState(false);
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      placeholder={placeholder}
      rows={4}
      style={{
        width: '100%',
        minHeight: 96,
        borderRadius: 18,
        padding: '12px 16px',
        fontSize: 14,
        color: 'var(--text-primary)',
        background: 'var(--input-bg)',
        border: focused
          ? '1px solid var(--text-secondary)'
          : '1px solid var(--input-border)',
        boxShadow: focused ? 'none' : 'var(--gem-input-shadow)',
        outline: 'none',
        transition: 'border-color 140ms, box-shadow 140ms',
        boxSizing: 'border-box',
        fontFamily:
          "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
        resize: 'vertical',
      }}
    />
  );
}

function ToggleSwitch({
  checked,
  onToggle,
}: {
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={onToggle}
      style={{
        all: 'unset',
        cursor: 'pointer',
        position: 'relative',
        width: 44,
        height: 26,
        borderRadius: 9999,
        background: checked ? 'var(--text-primary)' : 'rgba(0,0,0,0.12)',
        transition: 'background-color 150ms ease',
        flexShrink: 0,
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 3,
          insetInlineStart: checked ? 21 : 3,
          width: 20,
          height: 20,
          borderRadius: 10,
          background: 'var(--bg)',
          transition: 'inset-inline-start 150ms ease',
          boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
        }}
      />
    </button>
  );
}

function SourceCallout({
  icon,
  title,
  body,
  valueLabel,
  ctaLabel,
  onCta,
}: {
  icon: string;
  title: string;
  body: string;
  valueLabel?: string;
  ctaLabel?: string;
  onCta?: () => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        padding: 16,
        borderRadius: 20,
        background: 'rgba(0,0,0,0.04)',
      }}
    >
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 18,
            background: 'rgba(0,0,0,0.06)',
            color: 'var(--text-primary)',
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
          }}
        >
          <MaterialSymbol name={icon} size={18} />
        </div>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div
            style={{
              fontSize: 15,
              fontWeight: 500,
              color: 'var(--text-primary)',
              lineHeight: '20px',
              fontFamily:
                "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
            }}
          >
            {title}
          </div>
          <div
            style={{
              fontSize: 13,
              lineHeight: '18px',
              color: 'var(--text-tertiary)',
              fontFamily:
                "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
            }}
          >
            {body}
          </div>
          {valueLabel ? (
            <div
              style={{
                fontSize: 13,
                color: 'var(--text-primary)',
                fontFamily:
                  "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {valueLabel}
            </div>
          ) : null}
        </div>
      </div>
      {ctaLabel ? (
        <div>
          <NeutralButton icon="open_in_new" label={ctaLabel} onPress={onCta} />
        </div>
      ) : null}
    </div>
  );
}

function EmptyCard({
  icon,
  title,
  body,
  actionLabel,
  onAction,
}: {
  icon: string;
  title: string;
  body: string;
  actionLabel?: string;
  onAction?: () => void;
}) {
  const { isRTL } = useLocale();
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        padding: '24px 18px',
        borderRadius: 20,
        background: 'rgba(0,0,0,0.04)',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: 22,
          background: 'rgba(0,0,0,0.06)',
          color: 'var(--text-secondary)',
          display: 'grid',
          placeItems: 'center',
        }}
      >
        <MaterialSymbol name={icon} size={20} />
      </div>
      <div
        style={{
          fontSize: 15,
          fontWeight: 500,
          color: 'var(--text-primary)',
          fontFamily:
            "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
        }}
      >
        {title}
      </div>
      <div
        style={{
          fontSize: 13,
          color: 'var(--text-tertiary)',
          lineHeight: '18px',
          fontFamily:
            "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
        }}
      >
        {body}
      </div>
      {actionLabel ? (
        <div style={{ marginTop: 6 }}>
          <NeutralButton icon={isRTL ? 'arrow_forward' : 'arrow_back'} label={actionLabel} onPress={onAction} />
        </div>
      ) : null}
    </div>
  );
}

function RecordCard({
  title,
  body,
  meta,
  imageUrl,
  onEdit,
  onRemove,
  editLabel,
  removeLabel,
}: {
  title: string;
  body: string;
  meta?: string;
  imageUrl?: string;
  onEdit: () => void;
  onRemove: () => void;
  editLabel: string;
  removeLabel: string;
}) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        padding: 14,
        borderRadius: 20,
        background: 'rgba(0,0,0,0.04)',
      }}
    >
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div
            style={{
              fontSize: 15,
              fontWeight: 500,
              color: 'var(--text-primary)',
              fontFamily:
                "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
            }}
          >
            {title}
          </div>
          <div
            style={{
              fontSize: 13,
              lineHeight: '18px',
              color: 'var(--text-tertiary)',
              fontFamily:
                "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {body}
          </div>
          {meta ? (
            <div
              style={{
                fontSize: 12,
                color: 'var(--text-tertiary)',
                fontFamily:
                  "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
              }}
            >
              {meta}
            </div>
          ) : null}
        </div>
        {imageUrl ? (
          <img
            src={imageUrl}
            alt=""
            style={{
              width: 72,
              height: 72,
              borderRadius: 14,
              objectFit: 'cover',
              flexShrink: 0,
              background: 'rgba(0,0,0,0.06)',
            }}
          />
        ) : null}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <NeutralButton icon="edit" label={editLabel} onPress={onEdit} />
        <DestructiveButton icon="delete" label={removeLabel} onPress={onRemove} />
      </div>
    </div>
  );
}

function FormCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        padding: 16,
        borderRadius: 20,
        background: 'rgba(0,0,0,0.04)',
      }}
    >
      <div
        style={{
          fontSize: 14,
          fontWeight: 500,
          color: 'var(--text-primary)',
          fontFamily:
            "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
        }}
      >
        {title}
      </div>
      {children}
    </div>
  );
}

function NeutralButton({
  icon,
  label,
  onPress,
  loading = false,
}: {
  icon: string;
  label: string;
  onPress?: () => void;
  loading?: boolean;
}) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={onPress}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      disabled={loading}
      style={{
        all: 'unset',
        cursor: loading ? 'progress' : 'pointer',
        height: 40,
        padding: '0 16px',
        borderRadius: 9999,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        background: hover ? 'rgba(0,0,0,0.08)' : 'rgba(0,0,0,0.06)',
        color: 'var(--text-primary)',
        fontSize: 14,
        fontWeight: 500,
        transition: 'background-color 140ms',
        fontFamily:
          "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
        opacity: loading ? 0.65 : 1,
      }}
    >
      <MaterialSymbol name={icon} size={16} />
      {label}
    </button>
  );
}

function PrimaryButton({
  label,
  onPress,
  loading = false,
}: {
  label: string;
  onPress?: () => void;
  loading?: boolean;
}) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={onPress}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      disabled={loading}
      style={{
        all: 'unset',
        cursor: loading ? 'progress' : 'pointer',
        height: 40,
        padding: '0 18px',
        borderRadius: 9999,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        background: 'var(--text-primary)',
        color: 'var(--bg)',
        fontSize: 14,
        fontWeight: 500,
        opacity: loading ? 0.65 : hover ? 0.88 : 1,
        transition: 'opacity 140ms',
        fontFamily:
          "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
      }}
    >
      {label}
    </button>
  );
}

function GhostButton({
  label,
  onPress,
}: {
  label: string;
  onPress: () => void;
}) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={onPress}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        all: 'unset',
        cursor: 'pointer',
        height: 40,
        padding: '0 14px',
        borderRadius: 9999,
        display: 'inline-flex',
        alignItems: 'center',
        background: hover ? 'rgba(0,0,0,0.06)' : 'transparent',
        color: 'var(--text-secondary)',
        fontSize: 14,
        fontWeight: 500,
        transition: 'background-color 140ms',
        fontFamily:
          "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
      }}
    >
      {label}
    </button>
  );
}

function DestructiveButton({
  icon,
  label,
  onPress,
}: {
  icon: string;
  label: string;
  onPress: () => void;
}) {
  const [hover, setHover] = useState(false);
  return (
    <button
      type="button"
      onClick={onPress}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        all: 'unset',
        cursor: 'pointer',
        height: 40,
        padding: '0 16px',
        borderRadius: 9999,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        background: hover ? 'rgba(217,48,37,0.12)' : 'rgba(217,48,37,0.08)',
        color: '#d93025',
        fontSize: 14,
        fontWeight: 500,
        transition: 'background-color 140ms',
        fontFamily:
          "'Google Sans Text', 'Google Sans', Roboto, Arial, sans-serif",
      }}
    >
      <MaterialSymbol name={icon} size={16} />
      {label}
    </button>
  );
}
